import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CKEditorModule } from 'ckeditor4-angular';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SharedService } from 'src/app/shared/services/shared.service';
import {
  EmailType,
  ModalID,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
import { EmailTemplates } from 'src/app/shared/models/company';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-email-config',
  standalone: true,
  templateUrl: './email-config.component.html',
  styleUrl: './email-config.component.scss',
  imports: [ReactiveFormsModule, CKEditorModule, TooltipModule],
})
export class EmailConfigComponent implements OnInit, OnDestroy {
  emailDefaultsForm: FormGroup;
  emailTypes = Object.values(EmailType);
  emailTypeLabels: Record<string, string> = {
    [EmailType.ffl]: 'FFL Request',
    [EmailType.transfer]: 'FFL Transfer',
    [EmailType.gb_transfer]: 'GunBroker Transfer',
    [EmailType.form_3310]: 'Form 3310',
  };
  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    public sharedService: SharedService,
  ) {
    this.emailDefaultsForm = this.fb.group({});
    this.emailTypes.forEach((type) => {
      this.emailDefaultsForm.addControl(
        type,
        this.fb.group({
          subject: [''],
          body: [''],
          include_footer: [false],
          include_ffl: [false],
          include_sot: [false],
        }),
      );
    });
  }

  ngOnInit(): void {
    // Initial check
    const defaults = this.sharedService.company_info.email_templates;
    if (defaults) {
      this.emailDefaultsForm.patchValue(defaults);
    }

    // Update form control disabled states based on document uploads
    this.updateFormControlStates();

    // Subscribe to future updates
    this.subscriptions.add(
      this.sharedService.company_info_retrieved$.subscribe((info) => {
        if (info && info.email_templates) {
          this.emailDefaultsForm.patchValue(info.email_templates);
        }
        // Update form control disabled states when company info changes
        this.updateFormControlStates();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async saveDefaults() {
    this.sharedService.openStatusSpinner('Saving defaults');
    this.sharedService.company_info.email_templates =
      this.emailDefaultsForm.value;
    await this.sharedService.saveCompanyInformation(
      this.sharedService.company_info,
    );
    this.sharedService.closeStatusSpinner();
  }

  getGroup(type: string): FormGroup {
    return this.emailDefaultsForm.get(type) as FormGroup;
  }

  isFflDocumentUploaded(): boolean {
    return !!this.sharedService.company_info?.ffl_document;
  }

  isSotDocumentUploaded(): boolean {
    return !!this.sharedService.company_info?.sot_document;
  }

  private updateFormControlStates(): void {
    const isFflUploaded = this.isFflDocumentUploaded();
    const isSotUploaded = this.isSotDocumentUploaded();

    this.emailTypes.forEach((type) => {
      const group = this.getGroup(type);
      const fflControl = group.get('include_ffl');
      const sotControl = group.get('include_sot');

      if (fflControl) {
        if (!isFflUploaded) {
          fflControl.disable({ emitEvent: false });
          fflControl.setValue(false, { emitEvent: false });
        } else {
          fflControl.enable({ emitEvent: false });
        }
      }

      if (sotControl) {
        if (!isSotUploaded) {
          sotControl.disable({ emitEvent: false });
          sotControl.setValue(false, { emitEvent: false });
        } else {
          sotControl.enable({ emitEvent: false });
        }
      }
    });
  }
}
